'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import {
  MapPin,
  DollarSign,
  Clock,
  Ship,
  Bookmark,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils/formatting';
import type { JobPost } from '@/lib/types/models';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import GlassPanel from '@/components/dashboard/GlassPanel';

export interface JobCardProps {
  job: JobPost;
  onApply?: (jobId: string) => void;
  onSave?: (jobId: string) => void;
  isSaved?: boolean;
  className?: string;
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  onApply,
  onSave,
  isSaved = false,
  className,
}) => {
  const [saved, setSaved] = useState(isSaved);

  useEffect(() => {
    setSaved(isSaved);
  }, [isSaved]);

  const handleSave = () => {
    setSaved(!saved);
    onSave?.(String(job.id));
  };

  const handleApply = () => {
    onApply?.(String(job.id));
  };

  const getMatchBadgeVariant = (score: number): React.ComponentProps<typeof Badge>['variant'] => {
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    return 'default';
  };

  return (
    <GlassPanel
      noPadding
      className={cn(
        'group relative p-5 transition-all hover:shadow-md hover:border-neon-cyan/20',
        className
      )}
    >
      <div className="flex items-start gap-4">
        {/* Company Logo Placeholder */}
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#0f2a4a]">
          {job.companyLogoUrl ? (
            <Image
              src={job.companyLogoUrl}
              alt={job.companyName}
              width={48}
              height={48}
              className="rounded-lg object-cover"
            />
          ) : (
            <Ship className="h-6 w-6 text-neon-amber" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {job.title}
              </h3>
              <p className="mt-0.5 text-sm text-ash">{job.companyName}</p>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              className={cn(
                'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors sm:h-9 sm:w-9',
                saved
                  ? 'bg-amber-50 text-amber-500'
                  : 'text-muted hover:bg-card hover:text-ash'
              )}
              aria-label={saved ? 'Remove from saved' : 'Save job'}
            >
              <Bookmark
                className="h-5 w-5"
                fill={saved ? 'currentColor' : 'none'}
              />
            </button>
          </div>

          {/* Meta Info */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted">
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-muted" />
              {job.joiningPort || 'Location not specified'}
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5 text-muted" />
              {job.salaryDisplay
                || (job.salaryMinUsd !== null
                  ? `${job.salaryCurrency ?? '$'}${job.salaryMinUsd}${job.salaryPeriod === 'month' ? '/mo' : job.salaryPeriod === 'day' ? '/day' : '/trip'}${job.salaryMinUsd !== job.salaryMaxUsd ? ' (negotiable)' : ''}`
                  : 'Salary not specified')}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-muted" />
              {formatDate(job.createdAt)}
            </span>
          </div>

          {/* Tags */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge variant="info">{job.contractType}</Badge>
            {job.vesselType && <Badge variant="default">{job.vesselType.vesselTypeName}</Badge>}
            {job.matchScore !== undefined && job.matchScore > 0 && (
              <Badge variant={getMatchBadgeVariant(job.matchScore)}>
                {job.matchScore}% Match
              </Badge>
            )}
          </div>

          {/* Description */}
          {job.description && (
            <p className="mt-3 text-sm text-ash line-clamp-2">
              {job.description}
            </p>
          )}

          {/* Actions */}
          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={handleApply}
              className="shrink-0"
            >
              Apply Now
            </Button>
            <Button
              variant="outline"
              href={`/jobs/${job.id}`}
              className="shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Details
            </Button>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
};

export default JobCard;
